'use client';

import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useSDKStore } from '@/lib/sdk/store';
import { CreateKeyDialog } from './create-key-dialog';
import { KeyUsageStats } from './key-usage-stats';
import {
  Key,
  Plus,
  MoreVertical,
  RefreshCw,
  Trash2,
  BarChart3,
  AlertTriangle,
  CheckCircle2,
  Clock,
} from 'lucide-react';

export function SDKKeysPage() {
  const {
    keys,
    keysLoading,
    keysError,
    selectedKey,
    loadKeys,
    revokeKey,
    rotateKey,
    setSelectedKey,
    clearErrors,
  } = useSDKStore();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [showUsage, setShowUsage] = useState(false);

  useEffect(() => {
    loadKeys();
    return () => clearErrors();
  }, [loadKeys, clearErrors]);

  const handleRevoke = async (id: string, name: string) => {
    if (confirm(`Are you sure you want to revoke the API key "${name}"? This cannot be undone.`)) {
      try {
        await revokeKey(id);
      } catch (error) {
        // Error handled by store
      }
    }
  };

  const handleRotate = async (id: string, name: string) => {
    if (confirm(`Are you sure you want to rotate the API key "${name}"? The old key will stop working immediately.`)) {
      try {
        await rotateKey(id);
      } catch (error) {
        // Error handled by store
      }
    }
  };

  const handleViewUsage = (key: typeof keys[0]) => {
    setSelectedKey(key);
    setShowUsage(true);
  };

  if (keysLoading && keys.length === 0) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[300px] w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Key className="size-6" />
            SDK API Keys
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage API keys for SDK and CLI access
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="size-4 mr-2" />
          Create API Key
        </Button>
      </div>

      {keysError && (
        <Alert variant="destructive">
          <AlertTriangle className="size-4" />
          <AlertDescription>{keysError}</AlertDescription>
        </Alert>
      )}

      {/* Keys Table */}
      <Card>
        <CardHeader>
          <CardTitle>API Keys</CardTitle>
          <CardDescription>
            Use these keys to authenticate SDK and CLI requests
          </CardDescription>
        </CardHeader>
        <CardContent>
          {keys.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Key className="size-12 mx-auto mb-4 opacity-50" />
              <p>No API keys created yet</p>
              <p className="text-sm">Create your first API key to get started</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Key Prefix</TableHead>
                  <TableHead>Scopes</TableHead>
                  <TableHead>Last Used</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[80px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {keys.map((key) => (
                  <TableRow key={key.id}>
                    <TableCell className="font-medium">{key.name}</TableCell>
                    <TableCell className="font-mono text-sm">
                      {key.key_prefix}••••••••
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {key.scopes.slice(0, 2).map((scope) => (
                          <Badge key={scope} variant="secondary" className="text-xs">
                            {scope}
                          </Badge>
                        ))}
                        {key.scopes.length > 2 && (
                          <Badge variant="secondary" className="text-xs">
                            +{key.scopes.length - 2}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {key.last_used_at ? (
                        <span className="flex items-center gap-1">
                          <Clock className="size-3" />
                          {new Date(key.last_used_at).toLocaleDateString()}
                        </span>
                      ) : (
                        'Never'
                      )}
                    </TableCell>
                    <TableCell>
                      {key.is_active ? (
                        <Badge variant="outline" className="text-green-500 border-green-500">
                          <CheckCircle2 className="size-3 mr-1" />
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-red-500 border-red-500">
                          Revoked
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleViewUsage(key)}>
                            <BarChart3 className="size-4 mr-2" />
                            View Usage
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleRotate(key.id, key.name)}
                            disabled={!key.is_active}
                          >
                            <RefreshCw className="size-4 mr-2" />
                            Rotate Key
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleRevoke(key.id, key.name)}
                            className="text-destructive"
                            disabled={!key.is_active}
                          >
                            <Trash2 className="size-4 mr-2" />
                            Revoke Key
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Security Tips */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Security Best Practices</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="text-sm text-muted-foreground space-y-2">
            <li>• Never share API keys or commit them to version control</li>
            <li>• Use the minimum required scopes for each key</li>
            <li>• Rotate keys regularly and revoke unused keys</li>
            <li>• Use IP restrictions for production keys when possible</li>
            <li>• Monitor key usage for suspicious activity</li>
          </ul>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <CreateKeyDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      />

      {/* Usage Stats */}
      {showUsage && selectedKey && (
        <KeyUsageStats
          keyId={selectedKey.id}
          keyName={selectedKey.name}
          open={showUsage}
          onClose={() => {
            setShowUsage(false);
            setSelectedKey(null);
          }}
        />
      )}
    </div>
  );
}
